import React, { useState } from 'react';
import {
  Search,
  RefreshCw,
  Eye,
  RotateCw,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { useErrors, useUpdateError, useRetryProcessing } from '@/hooks/useDashboard';
import type { ErrorFilters, ErrorSeverity, ErrorStatus, ProcessingError } from '@/types/error';
import { ERROR_TYPE_LABELS, SEVERITY_COLORS, STATUS_COLORS } from '@/types/error';

export const ErrorsPage: React.FC = () => {
  const [filters, setFilters] = useState<ErrorFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedError, setSelectedError] = useState<ProcessingError | null>(null);

  const { data, isLoading, refetch, isFetching } = useErrors(filters);
  const updateErrorMutation = useUpdateError();
  const retryProcessingMutation = useRetryProcessing();

  // Use API data
  const errors = data?.errors ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 1;

  const handleStatusChange = async (errorId: string, newStatus: ErrorStatus) => {
    await updateErrorMutation.mutateAsync({
      id: errorId,
      data: { status: newStatus },
    });
    setSelectedError(null);
  };

  const handleRetry = async (errorId: string) => {
    await retryProcessingMutation.mutateAsync(errorId);
  };

  const handleFilterChange = (key: keyof ErrorFilters, value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset page to 1 only when changing filters, not when changing page itself
      ...(key !== 'page' && { page: 1 }),
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <PageLoader message="Carregando erros..." />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Erros de Processamento
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Monitore e gerencie erros do sistema
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          isLoading={isFetching}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Atualizar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por mensagem, usuario..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Severity filter */}
            <select
              value={filters.severity || ''}
              onChange={(e) =>
                handleFilterChange(
                  'severity',
                  e.target.value ? (e.target.value as ErrorSeverity) : undefined
                )
              }
              className="input w-full md:w-40"
            >
              <option value="">Severidade</option>
              <option value="critical">Critico</option>
              <option value="high">Alto</option>
              <option value="medium">Medio</option>
              <option value="low">Baixo</option>
            </select>

            {/* Status filter */}
            <select
              value={filters.status || ''}
              onChange={(e) =>
                handleFilterChange(
                  'status',
                  e.target.value ? (e.target.value as ErrorStatus) : undefined
                )
              }
              className="input w-full md:w-40"
            >
              <option value="">Status</option>
              <option value="new">Novo</option>
              <option value="investigating">Investigando</option>
              <option value="resolved">Resolvido</option>
              <option value="ignored">Ignorado</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Errors table */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-secondary-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Tipo
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Mensagem
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Severidade
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Data
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-secondary-700">
              {errors.map((err) => (
                <tr
                  key={err.id}
                  className="hover:bg-gray-50 dark:hover:bg-secondary-700/50"
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {ERROR_TYPE_LABELS[err.type] || err.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                      {err.message}
                    </p>
                    {err.userPhone && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Tel: {err.userPhone}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={SEVERITY_COLORS[err.severity]}>
                      {err.severity.charAt(0).toUpperCase() + err.severity.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={STATUS_COLORS[err.status]}>
                      {err.status === 'new'
                        ? 'Novo'
                        : err.status === 'investigating'
                        ? 'Investigando'
                        : err.status === 'resolved'
                        ? 'Resolvido'
                        : 'Ignorado'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(err.createdAt)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedError(err)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                        title="Ver detalhes"
                      >
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      {err.status !== 'resolved' && (
                        <button
                          onClick={() => handleRetry(err.id)}
                          disabled={retryProcessingMutation.isPending}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                          title="Tentar novamente"
                        >
                          <RotateCw className="w-4 h-4 text-blue-500" />
                        </button>
                      )}
                      {err.status !== 'resolved' && (
                        <button
                          onClick={() => handleStatusChange(err.id, 'resolved')}
                          disabled={updateErrorMutation.isPending}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                          title="Marcar como resolvido"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </button>
                      )}
                      {err.status === 'new' && (
                        <button
                          onClick={() => handleStatusChange(err.id, 'ignored')}
                          disabled={updateErrorMutation.isPending}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                          title="Ignorar"
                        >
                          <XCircle className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty state */}
          {errors.length === 0 && (
            <div className="py-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                Nenhum erro encontrado
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                O sistema esta funcionando normalmente.
              </p>
            </div>
          )}
        </div>

        {/* Pagination - only show when there are errors */}
        {errors.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-secondary-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Mostrando {errors.length} de {total} erros
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={(filters.page || 1) <= 1}
                onClick={() =>
                  handleFilterChange('page', (filters.page || 1) - 1)
                }
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Pagina {filters.page || 1} de {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={(filters.page || 1) >= totalPages}
                onClick={() =>
                  handleFilterChange('page', (filters.page || 1) + 1)
                }
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Error detail modal */}
      {selectedError && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader
              action={
                <button
                  onClick={() => setSelectedError(null)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-secondary-700 rounded"
                >
                  <XCircle className="w-5 h-5 text-gray-500" />
                </button>
              }
            >
              <CardTitle>Detalhes do Erro</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Tipo
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {ERROR_TYPE_LABELS[selectedError.type]}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      ID
                    </p>
                    <p className="font-mono text-sm text-gray-900 dark:text-white">
                      {selectedError.id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Severidade
                    </p>
                    <Badge className={SEVERITY_COLORS[selectedError.severity]}>
                      {selectedError.severity}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Status
                    </p>
                    <Badge className={STATUS_COLORS[selectedError.status]}>
                      {selectedError.status}
                    </Badge>
                  </div>
                  {selectedError.userPhone && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Telefone
                      </p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedError.userPhone}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Data
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(selectedError.createdAt)}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Mensagem
                  </p>
                  <p className="p-3 bg-gray-50 dark:bg-secondary-700/50 rounded-lg text-gray-900 dark:text-white">
                    {selectedError.message}
                  </p>
                </div>

                {selectedError.stackTrace && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      Stack Trace
                    </p>
                    <pre className="p-3 bg-gray-900 rounded-lg text-green-400 text-xs overflow-x-auto">
                      {selectedError.stackTrace}
                    </pre>
                  </div>
                )}

                {selectedError.details && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      Detalhes
                    </p>
                    <pre className="p-3 bg-gray-50 dark:bg-secondary-700/50 rounded-lg text-sm overflow-x-auto">
                      {JSON.stringify(selectedError.details, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-secondary-700">
                  {selectedError.status !== 'resolved' && (
                    <>
                      <Button
                        variant="primary"
                        onClick={() =>
                          handleStatusChange(selectedError.id, 'resolved')
                        }
                        isLoading={updateErrorMutation.isPending}
                        leftIcon={<CheckCircle className="w-4 h-4" />}
                      >
                        Marcar Resolvido
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => handleRetry(selectedError.id)}
                        isLoading={retryProcessingMutation.isPending}
                        leftIcon={<RotateCw className="w-4 h-4" />}
                      >
                        Tentar Novamente
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedError(null)}
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ErrorsPage;
